import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import type { IUser, IMfg } from 'models';

/** rest_auth.login when email MFA applies: the SPA leaves for /verify-mfa/
 * (VerifyMfa.tsx), which completes the login with verifyMfa. */
export interface LoginMfaRedirect {
  mfa_required: true;
  redirect: string;
}

/** rest_auth.login without MFA: the user, plus `next`, the server-checked
 * page to load now (the request's ?next= if on this host, else '/'). */
export type LoginSuccess = IUser & { next: string };

/** rest_auth.verify_mfa. A 400 carries `detail`, plus `restart: true` when
 * only a new sign-in can help (no login in progress, or no live code left). */
export interface VerifyMfaResult {
  redirect: string;
}

export interface ResetStatus {
  valid: boolean;
  username?: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['CurrentUser'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query<IUser, void>({
      query: () => '/api/v1.0/users/get_current/',
      providesTags: ['CurrentUser'],
    }),
    getMfg: builder.query<IMfg, number>({
      query: (id) => `/api/v1.0/mfg/${id}`,
    }),
    login: builder.mutation<
      LoginSuccess | LoginMfaRedirect,
      { username: string; password: string; remember?: boolean; next?: string }
    >({
      query: (credentials) => ({
        url: '/api/v1.0/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['CurrentUser'],
    }),
    verifyMfa: builder.mutation<VerifyMfaResult, { code: string; remember_device: boolean }>({
      query: (body) => ({ url: '/api/v1.0/auth/verify-mfa', method: 'POST', body }),
      invalidatesTags: ['CurrentUser'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({ url: '/api/v1.0/auth/logout', method: 'POST' }),
      invalidatesTags: ['CurrentUser'],
    }),
    forgotPassword: builder.mutation<{ submitted: boolean }, { email: string }>({
      query: (body) => ({ url: '/api/v1.0/auth/forgot-password', method: 'POST', body }),
    }),
    resetPasswordStatus: builder.query<ResetStatus, void>({
      query: () => '/api/v1.0/auth/reset-password/status',
    }),
    resetPassword: builder.mutation<
      { success: boolean },
      { new_password: string; confirm_password: string }
    >({
      query: (body) => ({ url: '/api/v1.0/auth/reset-password', method: 'POST', body }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetMfgQuery,
  useLoginMutation,
  useVerifyMfaMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordStatusQuery,
  useResetPasswordMutation,
} = authApi;
